import React, { useState } from "react";
import { Link } from "react-router-dom";
import Meta from "../components/Meta";

/* ─────────────────────────────────────────
   FAQ accordion item
───────────────────────────────────────── */
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-foreground/10 cursor-pointer select-none"
      onClick={() => setOpen((o) => !o)}
    >
      <div className="flex items-center justify-between py-4 px-2 gap-4">
        <span className="text-sm font-semibold leading-snug">{q}</span>
        <span
          className="flex-shrink-0 text-lg font-black transition-transform duration-200"
          style={{
            transform: open ? "rotate(45deg)" : "rotate(0deg)",
            color: "#fc6767",
          }}
        >
          +
        </span>
      </div>
      {open && (
        <p className="px-2 pb-4 text-sm text-foreground/70 leading-relaxed">
          {a}
        </p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   Shared CTA button
───────────────────────────────────────── */
const CtaButton = ({ text = "Generate Free →", className = "" }) => (
  <Link
    to="/create"
    className={`group relative inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-8 py-3 text-base font-black text-white transition hover:scale-105 active:scale-110 ${className}`}
    style={{ background: "linear-gradient(135deg,#1a0010,#ec008c,#fc6767)" }}
  >
    <span className="absolute inset-0 rounded-lg opacity-0 transition group-hover:opacity-20"
      style={{ background: "white" }} />
    {text}
  </Link>
);

/* ─────────────────────────────────────────
   Content sections data
───────────────────────────────────────── */
const sections = [
  {
    id: "what-is",
    label: "01",
    heading: "What is an AI Blowjob Generator?",
    keywords: ["ai blowjob generator", "blowjob art", "create blowjob ai"],
    images: [
      { src: "./fr-2.png",    alt: "Realistic AI blowjob image",   rotate: -3, z: 10 },
      { src: "./fr-5.png",    alt: "AI generated blowjob art",      rotate: 4,  z: 20 },
      { src: "./anime-1.png", alt: "Anime AI blowjob illustration",  rotate: -6, z: 5  },
    ],
    body: [
      "An AI blowjob generator is an artificial intelligence tool that creates explicit oral sex images or video-style content entirely from text prompts — no real people involved, no studio, no camera. You type what you want to see, and the model generates it from scratch.",
      "The underlying technology is a diffusion model trained on massive datasets of artistic and photographic content. When you enter a prompt describing a blowjob scene — lighting, setting, character details, angles — the model interprets every word and synthesizes a unique image or animated sequence that matches your description.",
      "Unlike searching content sites or hoping to find something close to your fantasy, an AI blowjob image generator produces exactly what you describe. Specific hair color, body type, setting, POV angle, facial expression — all of it is in your control. The result is custom blowjob art built to your exact specifications.",
      "NSFWArtGenerator.ai specializes in this exact type of explicit AI generation, with models tuned specifically for adult content that most mainstream tools censor or refuse entirely.",
    ],
  },
  {
    id: "can-ai-generate",
    label: "02",
    heading: "Can AI Generate Realistic Adult Images?",
    keywords: ["realistic blowjob", "ai generated blowjob", "blowjob fantasies"],
    images: [
      { src: "./fr-4.png",    alt: "Photorealistic AI adult image",  rotate: 2,  z: 20 },
      { src: "./fr-1.png",    alt: "Realistic AI generated image",   rotate: -5, z: 10 },
      { src: "./anime-3.png", alt: "Anime vs realistic comparison",  rotate: 6,  z: 5  },
    ],
    body: [
      "Yes — and the gap between AI-generated and real photography has narrowed dramatically. Modern diffusion models produce skin texture, subsurface light scattering, facial expressions, and anatomical detail that looks indistinguishable from a real photograph at first glance.",
      "For blowjob scenes specifically, the AI handles the most challenging visual elements: wet lips, realistic skin-on-skin contact, natural lighting on faces and bodies, believable eye contact and facial expressions mid-act. When combined with a detailed prompt — lighting style, focal length, skin tone, setting — the output consistently looks photorealistic.",
      "Anime and illustrated styles are equally convincing within their aesthetic. Hentai-style blowjob art generated by AI captures exaggerated expressions, stylized proportions, and the exact visual language of the genre — clean linework, gradient shading, vibrant colors — with no compromise.",
      "The key to maximizing realism is prompt quality. Adding terms like 'RAW photo', 'shallow depth of field', 'shot on Sony A7R', 'skin pores visible', and 'cinematic lighting' signals the model to prioritize photorealistic output over illustrated or stylized results.",
    ],
  },
  {
    id: "best-free",
    label: "03",
    heading: "What is the Best Free AI Blowjob Generator?",
    keywords: ["free ai blowjob generator", "ai blowjob image generator", "blowjob art"],
    images: [
      { src: "./fr-6.png",    alt: "Free AI blowjob generator output",  rotate: -4, z: 20 },
      { src: "./fr-3.png",    alt: "AI blowjob image generator result", rotate: 5,  z: 10 },
      { src: "./anime-5.png", alt: "Free anime blowjob AI art",         rotate: -2, z: 5  },
    ],
    body: [
      "NSFWArtGenerator.ai is the leading free AI blowjob generator, built specifically for explicit adult content without the restrictions that make mainstream AI tools unusable for this purpose. Most popular AI image generators — Midjourney, DALL·E, Stable Diffusion via standard interfaces — refuse NSFW prompts outright. This platform does not.",
      "Free users receive generation credits on signup with no credit card required, giving immediate access to realistic blowjob image generation across multiple styles: photorealistic, anime/hentai, 3D CGI, and illustrated. The free tier is genuinely usable — not a stripped-down demo.",
      "What separates this generator from lower-quality alternatives is the model training. The underlying model has been specifically fine-tuned on adult content, which means it understands blowjob scene composition, oral anatomy, realistic expressions mid-act, and explicit positioning in a way generic models simply do not.",
      "For users who want unlimited AI blowjob image generation with higher resolution, faster processing, and no daily caps, premium plans are available — but the free tier is a fully functional starting point.",
    ],
  },
  {
    id: "video",
    label: "04",
    heading: "Can I Generate AI Blowjob Videos?",
    keywords: ["ai blowjob video generator", "blowjob under desk"],
    images: [
      { src: "./anime-2.png", alt: "AI blowjob video frame",        rotate: 3,  z: 20 },
      { src: "./anime-4.png", alt: "AI animated blowjob scene",     rotate: -5, z: 10 },
      { src: "./anime-6.png", alt: "AI blowjob under desk scene",   rotate: 4,  z: 5  },
    ],
    body: [
      "AI blowjob video generation is an emerging capability. Current technology produces high-quality still images and short animated sequences rather than full-length video. The generator on this platform focuses on creating photorealistic and stylized blowjob images, with video generation functionality on the active development roadmap.",
      "What is available now is high-resolution image generation that captures frozen moments of blowjob scenes in extraordinary detail — mid-act expressions, realistic positioning, depth of field, cinematic lighting. For scene-specific fantasies like 'blowjob under desk', 'POV blowjob', or 'outdoor setting', the image output is detailed and composition-aware.",
      "AI video generation for adult content currently exists in early-stage tools, but quality is inconsistent and outputs are short (typically 3–6 seconds). As the technology matures rapidly, video generation at this platform will be enabled. Users on the waitlist will be notified when it launches.",
      "For now, generating a sequence of related still images — each with the same character and setting, progressing through a scene — is the most effective way to create a visual narrative using the current toolset.",
    ],
  },
  {
    id: "prompts-work",
    label: "05",
    heading: "How Do AI Blowjob Prompts Work?",
    keywords: ["ai blowjob prompt", "how do prompts work", "create blowjob ai"],
    images: [
      { src: "./fr-1.png",    alt: "AI blowjob prompt result realistic",  rotate: -3, z: 5  },
      { src: "./anime-1.png", alt: "AI blowjob prompt anime output",      rotate: 5,  z: 10 },
      { src: "./fr-3.png",    alt: "AI blowjob create from prompt",       rotate: -2, z: 20 },
    ],
    body: [
      "A prompt is a text instruction that tells the AI exactly what to generate. For blowjob scenes, the prompt is your complete creative brief — every detail you include becomes part of the image. The model reads the entire prompt and weights each element when building the output.",
      "Effective blowjob prompts have three layers: the act itself, the characters, and the environment. Describing all three produces the most controlled results. Example: 'POV blowjob, woman with long dark hair, on knees, eye contact, studio lighting, photorealistic, 8K' gives the AI clear direction on action, character, angle, and style simultaneously.",
      "Negative prompts — words you want to exclude — are equally important. Adding terms like 'blurry, deformed, bad anatomy, cartoon' to your negative prompt prevents the most common AI generation errors and pushes the output toward the quality level you expect.",
      "Style keywords dramatically change the output. 'RAW photo' and 'photorealistic' push toward hyper-realism. 'Anime illustration' or 'hentai style' switches to the stylized aesthetic. 'Oil painting' or 'digital art' gives an illustrated feel. Combining a detailed scene description with a clear style directive consistently produces the best results.",
    ],
  },
  {
    id: "best-prompts",
    label: "06",
    heading: "Which Prompts Create the Most Realistic Results?",
    keywords: ["realistic blowjob", "ai blowjob prompt", "blowjob fantasies"],
    prompts: [
      {
        label: "Hyper-Realistic POV",
        text: "POV blowjob, woman on knees looking up, eye contact, wet lips, shallow depth of field, shot on Canon EOS R5, photorealistic skin texture, soft studio lighting, 8K RAW photo",
      },
      {
        label: "Outdoor / Under Desk",
        text: "blowjob under desk, office setting, hidden, cinematic angle, realistic, natural lighting through blinds, photorealistic, detailed facial expression, 4K",
      },
      {
        label: "Anime / Hentai Style",
        text: "anime girl giving blowjob, ahegao expression, huge eyes, smooth gradient shading, ecchi style, detailed linework, blush marks, vibrant colors, high-res illustration",
      },
      {
        label: "3D CGI",
        text: "3D CGI render, explicit blowjob scene, subsurface skin scattering, studio lighting, ultra-smooth skin, octane render, cinematic camera angle, 4K resolution",
      },
      {
        label: "Fantasy Roleplay",
        text: "fantasy setting, elf giving blowjob, moonlit forest, magical glow, revealing outfit partially removed, detailed facial expression, digital painting, cinematic composition",
      },
      {
        label: "Realistic Close-Up",
        text: "close-up blowjob, realistic lips, wet, detailed skin texture, macro lens effect, warm candlelight, photorealistic, shallow focus, 8K",
      },
    ],
  },
];

/* ─────────────────────────────────────────
   FAQ data
───────────────────────────────────────── */
const faqs = [
  {
    category: "About the Generator",
    items: [
      { q: "What does this AI generator create?", a: "It generates explicit adult images depicting blowjob and oral sex scenes from text prompts. Every image is synthesized from scratch by AI — no real people are involved. Outputs span photorealistic, anime, 3D CGI, and illustrated styles depending on the style you select and keywords you use in your prompt." },
      { q: "Is it image-based or video-based?", a: "Currently the generator produces high-resolution still images. Video generation is on the roadmap and in active development. For now, you can generate sequences of images depicting a scene from different moments or angles, which approximates a visual narrative until full video generation is available." },
      { q: "How long are the videos?", a: "Full video generation is not yet available on this platform. The current tool generates high-quality static images. When AI blowjob video generation launches, initial clips will likely be short-form (5–15 seconds) as is standard across current AI video technology, with longer outputs following as the technology matures." },
      { q: "Is it realistic?", a: "Yes — the model is specifically tuned for adult content realism. It accurately renders oral anatomy, skin texture, facial expressions, lighting on skin, and scene composition. Adding realism keywords to your prompt — 'RAW photo', '8K', 'shot on Sony A7R', 'skin pores visible' — pushes the output further toward photographic quality." },
    ],
  },
  {
    category: "Free Access & Pricing",
    items: [
      { q: "Is it free?", a: "Yes. You receive free generation credits when you create an account, with no credit card required. The free tier gives you enough generations to explore multiple styles and prompts. A paid plan is available for users who want unlimited daily generations, higher resolution, and faster processing speeds." },
      { q: "Do I need to sign up?", a: "A free account is required to generate images. Registration takes under a minute and requires only an email address. You do not need to provide a phone number, credit card, or any payment information to access the free tier and start generating AI blowjob images immediately." },
      { q: "Is there a free trial?", a: "Yes — the free credits you receive on signup function as a trial. There is no time limit on when you use them. You can generate several images, test different styles and prompts, and explore the platform fully before deciding whether to upgrade to a paid plan." },
      { q: "How many generations are free?", a: "Free accounts receive enough credits for several generations. The exact number is shown on your account dashboard after signup. Credits are sufficient to test all major styles (realistic, anime, 3D) and iterate on prompts to get results you're satisfied with before your free credits run out." },
      { q: "Are credits required?", a: "Credits are the generation currency on the platform. Free credits are given at signup. Once free credits are used, additional generations require purchasing a credit pack or subscribing to a plan. Credits do not expire and can be used at any time across all generation types and styles." },
      { q: "Are outputs watermarked?", a: "No. Images generated on this platform — on both free and paid plans — are delivered without watermarks. Your downloaded image is clean and full-resolution. We do not embed visible or invisible watermarks into generated adult content." },
    ],
  },
  {
    category: "Customization",
    items: [
      { q: "Is it uncensored?", a: "Yes. Unlike mainstream AI tools that refuse explicit prompts, this platform is built specifically for uncensored adult content generation. Blowjob scenes, oral sex imagery, and explicit positioning are all supported without censorship bars, blurring, or content refusals." },
      { q: "Can I customize the scene?", a: "Completely. You describe every element — character appearance, hair color, skin tone, body type, setting, lighting, camera angle, level of explicitness, clothing or lack of it, and facial expression. The more detail you provide in your prompt, the more accurately the AI renders exactly what you have in mind." },
      { q: "Can it generate POV scenes?", a: "Yes. Adding 'POV' to your prompt generates first-person perspective blowjob scenes where the action is framed from the viewer's point of view. Combine with 'looking up at camera', 'eye contact', and 'realistic' for the most immersive POV blowjob image outputs." },
      { q: "Can AI generate specific scenarios / fantasies?", a: "Yes. The AI understands scene context and setting. Describe your exact fantasy — 'blowjob under desk at office', 'outdoor by pool', 'fantasy dungeon setting', 'car scene', 'hotel room' — and the model incorporates the environment, lighting, and mood of that setting into the generated image." },
      { q: "Can it create office / fantasy roleplay scenes?", a: "Absolutely. Office scenarios like 'secretary blowjob under desk' or 'boss fantasy' are well-supported. Fantasy roleplay — elf, sorceress, demon, maid, nurse — are equally effective. Describe the costume, setting, and context and the AI renders the full roleplay scene with accurate details." },
      { q: "Can I choose poses and camera angles?", a: "Yes. Specify the exact pose ('on knees', 'lying down', 'standing') and camera angle ('POV', 'side view', 'close-up', 'wide shot', 'overhead') in your prompt. The model respects these directions and composes the image accordingly. Combining pose and angle descriptors gives you precise control over the final composition." },
      { q: "Can I create custom characters?", a: "Yes, entirely through prompt description. Specify hair color and length, eye color, skin tone, ethnicity, body type, breast size, and age range. While you cannot save a persistent character profile yet, including a detailed character description in every prompt produces consistent-looking results across multiple generations." },
    ],
  },
  {
    category: "Image Quality",
    items: [
      { q: "How fast does it generate?", a: "Most generations complete in 8–15 seconds. Speed depends on server load and your selected style. Realistic and 3D CGI styles may take slightly longer than anime or illustrated styles. Premium users get priority queue access, which reduces wait times significantly during peak usage periods." },
      { q: "Can I generate unlimited images?", a: "Free users have a daily generation limit. Premium subscribers get unlimited daily generations with no cap. There is no restriction on the type of content you generate within the platform's guidelines — you can generate as many blowjob scenes as your plan allows without any per-style or per-topic restrictions." },
      { q: "Does it look photorealistic or animated?", a: "Both are available depending on the style you select and keywords in your prompt. The Hyper-Realistic style produces photographic-quality blowjob images with real-looking skin and lighting. The Anime style produces stylized illustrated outputs. Your prompt keywords reinforce the style — 'RAW photo' vs 'anime illustration' significantly shifts the output." },
      { q: "Can AI create believable facial expressions?", a: "Yes, including mid-act expressions which are among the most technically challenging for AI to render. The model understands contextual facial expressions — eye contact, open mouth, ahegao, blush — and applies them accurately when specified in prompts. Adding 'realistic facial expression' or specific expression descriptors improves this further." },
      { q: "Does it look like real video?", a: "Still images can be rendered at a quality level that resembles a single frame from a professional video production — with proper lighting, depth of field, and motion blur implied in the composition. Full motion video generation is not yet available, but image quality is consistently mistakable for a real photograph or video still." },
      { q: "Does it include sound / audio?", a: "No. The current generator produces visual content only — still images and, in future, silent video clips. Audio generation is not currently part of the platform's capabilities. When AI video generation launches, initial outputs will be silent, with audio synthesis potentially added in a later phase of development." },
      { q: "Is the video quality HD or 4K?", a: "Image generation is available at high resolution. Adding '4K', '8K', or 'ultra-high resolution' to your prompt signals the model to maximize output detail. Premium plans support higher-resolution outputs than the free tier. When video generation launches, HD quality will be the baseline for premium users." },
    ],
  },
  {
    category: "Face Swap & Photo Input",
    items: [
      { q: "Can I upload my own image?", a: "Image-to-image generation — where you upload a reference photo and the AI transforms or builds on it — is on the active development roadmap. The current tool generates images entirely from text prompts. When img2img functionality launches, users will be able to upload reference images and apply blowjob scene generation on top of them." },
      { q: "Can AI transform a photo into a generated scene?", a: "Not yet with uploaded photos, but this feature is in development. Currently, you can achieve a similar effect by describing the appearance of a real person in precise detail within your prompt — hair, facial features, body type, skin tone — and the AI renders a similar-looking character in the scene you describe." },
      { q: "Does face swap work?", a: "Face swap is not currently available as a standalone feature. It is on the product roadmap. When released, it will allow users to apply a face reference onto an AI-generated body in a blowjob scene. This is one of the most-requested features and is in active development for a near-term release." },
      { q: "Can I use selfies or avatars?", a: "Direct selfie or avatar upload is not yet supported. However, you can describe your own features or a character's likeness in detail within your text prompt, and the AI will generate a character matching that description. When image-upload functionality launches, selfie-based generation will be one of the primary use cases." },
    ],
  },
  {
    category: "Privacy & Data",
    items: [
      { q: "Is it anonymous / private?", a: "Yes. Your account requires only an email to create. We do not collect your name, phone number, or any identifying information beyond your email address. Your prompts, generated images, and browsing activity on the platform are private and are never shared with third parties or used for external profiling." },
      { q: "Will anyone see my content?", a: "No. There is no public feed, community gallery, or discovery section where your generated images appear to other users. Everything you generate is visible only to your account. We do not display, share, or sell user-generated adult content under any circumstances." },
      { q: "Is generation history private?", a: "Yes. Your generation history — every prompt and image you have created — is private to your account and visible only to you. We do not audit or review individual user generation histories unless required by law. Your history can be cleared from your account settings at any time." },
      { q: "Are prompts saved?", a: "Prompts may be temporarily held in session memory to support regeneration and iteration within a session. They are not permanently logged in a way that is associated with your identity or browsable by staff. You can clear your session data and generation history at any time from your account dashboard." },
      { q: "Can I delete my data?", a: "Yes. You can delete individual generated images, your full generation history, and your entire account from the settings page. Account deletion removes all associated data — email, history, saved images, and credits — permanently from our systems. Deletion requests are processed within 48 hours." },
      { q: "Is it private?", a: "The platform uses HTTPS encryption on all pages and API calls. Your prompts, generated images, and account data are transmitted and stored securely. We do not sell user data, share generation history with advertisers, or retain content that you have not explicitly saved to your private gallery." },
    ],
  },
];

/* ─────────────────────────────────────────
   Page component
───────────────────────────────────────── */
const AiBlowjobGenerator = () => {
  return (
    <>
      <Meta
        title="AI Blowjob Generator – Free Realistic & Anime Blowjob Images"
        description="Generate explicit AI blowjob images in seconds. Realistic, anime, POV, 3D CGI — describe your fantasy and the AI creates it. Free to start, no credit card required."
        meta={{ robots: "index, follow" }}
      />

      <main className="flex-auto bg-background-body">

        {/* ── HERO ── */}
        <section
          className="relative min-h-[92vh] flex flex-col items-center justify-center text-center px-4 overflow-hidden"
          style={{ background: "radial-gradient(ellipse at 60% 40%, #2d001a 0%, #0d000a 70%)" }}
        >
          {/* Background glow blobs */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 left-1/4 h-96 w-96 rounded-full opacity-20 blur-3xl"
              style={{ background: "radial-gradient(circle, #ec008c, transparent)" }} />
            <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full opacity-15 blur-3xl"
              style={{ background: "radial-gradient(circle, #fc6767, transparent)" }} />
          </div>

          {/* Badge */}
          <div className="relative mb-6 inline-flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-4 py-1.5 text-xs font-semibold text-pink-300 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-pink-400 animate-pulse" />
            Free AI Blowjob Generator · No Sign-Up Required
          </div>

          {/* Headline */}
          <h1 className="relative mb-4 max-w-4xl text-5xl md:text-7xl font-black leading-tight tracking-tight">
            Generate{" "}
            <span
              className="!bg-clip-text text-transparent"
              style={{ background: "linear-gradient(135deg,#ff6fb5,#fc6767,#ffb347)" }}
            >
              AI Blowjob
            </span>
            <br />
            Images Instantly
          </h1>

          <p className="relative mb-8 max-w-xl text-base md:text-lg text-foreground/60 leading-relaxed">
            Type your fantasy. Pick a style. The AI generates a realistic blowjob image in under 10 seconds —
            uncensored, private, no watermark. Realistic, anime, POV, 3D and more.
          </p>

          <div className="relative flex flex-col sm:flex-row items-center gap-4 mb-10">
            <CtaButton text="Generate AI Blowjob Free →" className="text-base px-10 py-4" />
            <span className="text-xs text-foreground/40">No credit card · Instant access · 100% private</span>
          </div>

          {/* Stats row */}
          <div className="relative flex flex-wrap justify-center gap-8 text-center">
            {[
              { value: "2M+", label: "Images Generated" },
              { value: "< 10s", label: "Generation Time" },
              { value: "6+", label: "Art Styles" },
              { value: "0", label: "Watermarks" },
            ].map(({ value, label }) => (
              <div key={label} className="flex flex-col items-center">
                <span className="text-2xl font-black"
                  style={{ background: "linear-gradient(135deg,#ff6fb5,#fc6767)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {value}
                </span>
                <span className="text-xs text-foreground/50 mt-0.5">{label}</span>
              </div>
            ))}
          </div>

          {/* Style pills */}
          <div className="relative mt-8 flex flex-wrap justify-center gap-2">
            {["Realistic", "Anime / Hentai", "POV", "3D CGI", "Under Desk", "Fantasy Roleplay", "Close-Up"].map((s) => (
              <span key={s} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-foreground/60 backdrop-blur-sm">
                {s}
              </span>
            ))}
          </div>
        </section>

        {/* ── CONTENT SECTIONS ── */}
        {sections.map((section, idx) => {
          const isEven = idx % 2 === 0;
          const hasImage = !!section.images;
          const imageLeft = isEven;

          return (
            <section
              key={section.id}
              id={section.id}
              className={`py-16 px-4 ${isEven ? "bg-black/30" : "bg-black/50"}`}
            >
              <div className="mx-auto max-w-5xl">

                {/* Prompt cards section — full width, no image */}
                {section.prompts && (
                  <>
                    <div className="mb-8 flex items-start gap-4">
                      <span
                        className="hidden sm:flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-xl text-sm font-black text-white"
                        style={{ background: "linear-gradient(135deg,#ec008c,#fc6767)" }}
                      >
                        {section.label}
                      </span>
                      <div>
                        {section.keywords && (
                          <div className="mb-2 flex flex-wrap gap-1">
                            {section.keywords.map((kw) => (
                              <span key={kw} className="rounded-full bg-pink-500/10 border border-pink-500/20 px-2 py-0.5 text-xs text-pink-400">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                        <h2 className="text-2xl md:text-3xl font-black leading-snug">{section.heading}</h2>
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {section.prompts.map(({ label, text }) => (
                        <div key={label} className="rounded-xl border border-foreground/10 bg-background/40 p-5 hover:bg-background/70 transition">
                          <span
                            className="mb-3 inline-block rounded-full px-3 py-0.5 text-xs font-bold text-white"
                            style={{ background: "linear-gradient(135deg,#ec008c,#fc6767)" }}
                          >
                            {label}
                          </span>
                          <p className="font-mono text-sm text-foreground/80 leading-relaxed">"{text}"</p>
                          <Link to="/create" className="mt-3 inline-block text-xs font-bold text-pink-400 hover:text-pink-300 transition">
                            Use this prompt →
                          </Link>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8"><CtaButton text="Try It Free →" /></div>
                  </>
                )}

                {/* Alternating image + content sections */}
                {hasImage && (
                  <div className={`flex flex-col gap-10 md:flex-row md:items-center ${imageLeft ? "" : "md:flex-row-reverse"}`}>

                    {/* Image collage side */}
                    <div className="w-full md:w-2/5 flex-shrink-0">
                      <div className="relative h-80 md:h-96">
                        {section.images.map((img, i) => {
                          const offsets = [
                            { top: "0%",  left: "0%",   width: "62%" },
                            { top: "15%", left: "38%",  width: "58%" },
                            { top: "42%", left: "8%",   width: "50%" },
                          ];
                          const pos = offsets[i] || offsets[0];
                          return (
                            <div
                              key={i}
                              className="absolute overflow-hidden rounded-xl border-2 border-white/15 shadow-xl shadow-black/50"
                              style={{
                                top: pos.top,
                                left: pos.left,
                                width: pos.width,
                                zIndex: img.z,
                                transform: `rotate(${img.rotate}deg)`,
                              }}
                            >
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10" />
                              <img
                                src={img.src}
                                alt={img.alt}
                                className="w-full"
                                style={{
                                  aspectRatio: "3/4",
                                  objectFit: "cover",
                                  objectPosition: "center 55%",
                                  transform: "scale(1.7)",
                                  transformOrigin: "center 52%",
                                }}
                              />
                            </div>
                          );
                        })}
                        {/* Number badge */}
                        <div className="absolute bottom-2 right-2 z-30">
                          <span
                            className="rounded-full px-3 py-1 text-xs font-black text-white shadow-lg"
                            style={{ background: "linear-gradient(135deg,#ec008c,#fc6767)" }}
                          >
                            {section.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content side */}
                    <div className="flex-1">
                      {section.keywords && (
                        <div className="mb-3 flex flex-wrap gap-1">
                          {section.keywords.map((kw) => (
                            <span key={kw} className="rounded-full bg-pink-500/10 border border-pink-500/20 px-2 py-0.5 text-xs text-pink-400">
                              {kw}
                            </span>
                          ))}
                        </div>
                      )}
                      <h2 className="mb-5 text-2xl md:text-3xl font-black leading-snug">{section.heading}</h2>
                      <div className="space-y-4 text-foreground/70 text-sm md:text-base leading-relaxed">
                        {section.body.map((para, i) => <p key={i}>{para}</p>)}
                      </div>
                      <div className="mt-7"><CtaButton text="Generate Free →" /></div>
                    </div>

                  </div>
                )}

              </div>
            </section>
          );
        })}

        {/* ── FAQ ── */}
        <section className="py-16 px-4 bg-black/30">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-2 text-center text-3xl md:text-4xl font-black">
              Frequently Asked Questions
            </h2>
            <p className="mb-12 text-center text-foreground/50 text-sm">
              Everything you need to know about the AI Blowjob Generator.
            </p>

            <div className="grid gap-10 md:grid-cols-2">
              {faqs.map(({ category, items }) => (
                <div key={category}>
                  <h3
                    className="mb-4 text-sm font-black uppercase tracking-widest"
                    style={{ color: "#fc6767" }}
                  >
                    {category}
                  </h3>
                  <div className="rounded-xl border border-foreground/10 overflow-hidden bg-background/30">
                    {items.map(({ q, a }) => (
                      <FaqItem key={q} q={q} a={a} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── RELATED TOOLS ── */}
        <section className="py-12 px-4 bg-black/40">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-6 text-center text-xl font-black text-foreground/80">Explore More AI Generators</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Link
                to="/create/ai-boobs"
                className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5 transition hover:border-pink-500/40 hover:bg-white/10"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-pink-600 to-rose-800 text-2xl">🍒</div>
                <div>
                  <div className="font-bold group-hover:text-pink-400 transition">AI Boobs Generator</div>
                  <div className="text-xs text-foreground/50">Generate any bust style — realistic, anime, 3D</div>
                </div>
                <span className="ml-auto text-foreground/30 group-hover:text-pink-400 transition">→</span>
              </Link>
              <Link
                to="/ai-nude"
                className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5 transition hover:border-purple-500/40 hover:bg-white/10"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-700 to-indigo-900 text-2xl">🔥</div>
                <div>
                  <div className="font-bold group-hover:text-purple-400 transition">AI Nude Generator</div>
                  <div className="text-xs text-foreground/50">Generate full nude AI images in any style</div>
                </div>
                <span className="ml-auto text-foreground/30 group-hover:text-purple-400 transition">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section
          className="relative py-20 px-4 text-center overflow-hidden"
          style={{ background: "radial-gradient(ellipse at center, #2d001a 0%, #0d000a 80%)" }}
        >
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full opacity-25 blur-3xl"
              style={{ background: "radial-gradient(circle, #ec008c, transparent)" }} />
          </div>
          <div className="relative mx-auto max-w-2xl">
            <h2 className="mb-3 text-3xl md:text-4xl font-black">
              Your fantasy. Rendered in 10 seconds.
            </h2>
            <p className="mb-8 text-foreground/50 text-base">
              Realistic, anime, POV, under desk, fantasy roleplay — any scene, any style.
              Free credits included. No card required.
            </p>
            <CtaButton text="Generate AI Blowjob Free →" className="text-lg px-10 py-4" />
          </div>
        </section>

      </main>
    </>
  );
};

export default AiBlowjobGenerator;
